import type { Product, User, UserBalances } from 'payments-api/model';
import clsx from 'clsx';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { useMemo } from 'react';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import {
  kLabelColumnWidth,
  LayoutRow,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';

import type { ItemTableItem } from './ItemTable';
import { FormatBillingAmount } from './formatBillingAmount';
import { ItemTable } from './ItemTable';

export type InvoiceTotals = {
  totalAmount: { amount: number; currency: CurrencyCode }[];
  isTotalBalanced: boolean;
};

export const getTotalsFromItems = (items: ItemTableItem[]): InvoiceTotals => {
  const totalPayinAmount = items
    .filter((item) => item.type === InvoiceLineItemType.payin)
    .reduce((acc, item) => {
      const code = item.currency_code as CurrencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const totalPayoutAmount = items
    .filter((item) => item.type === InvoiceLineItemType.payout)
    .reduce((acc, item) => {
      const code = item.currency_code as CurrencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const allCurrencies = new Set([
    ...Object.keys(totalPayinAmount),
    ...Object.keys(totalPayoutAmount),
  ]);
  const isTotalBalanced = Array.from(allCurrencies).every(
    (currency) =>
      (totalPayinAmount[currency as CurrencyCode] || 0) ===
      (totalPayoutAmount[currency as CurrencyCode] || 0)
  );

  const totalAmount = Object.entries(totalPayinAmount).map(
    ([currency, amount]) => ({
      amount: Number(amount),
      currency: currency as CurrencyCode,
    })
  );

  return { totalAmount, isTotalBalanced };
};

/**
 * Totals for the amount row when viewing as a specific user.
 * - If user has both payin and payout: total = payin - payout per currency.
 * - Otherwise: total = absolute value of payin total or payout total per currency.
 */
export const getTotalsForUserView = (
  items: ItemTableItem[]
): {
  amount: number;
  currency: CurrencyCode;
  payinSum: number;
  payoutSum: number;
}[] => {
  const totalPayin = items
    .filter((item) => item.type === InvoiceLineItemType.payin)
    .reduce((acc, item) => {
      const code = item.currency_code as CurrencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const totalPayout = items
    .filter((item) => item.type === InvoiceLineItemType.payout)
    .reduce((acc, item) => {
      const code = item.currency_code as CurrencyCode;
      acc[code] = (acc[code] || 0) + Number(item.amount);
      return acc;
    }, {} as Record<CurrencyCode, number>);

  const hasPayin = items.some(
    (item) => item.type === InvoiceLineItemType.payin
  );
  const hasPayout = items.some(
    (item) => item.type === InvoiceLineItemType.payout
  );
  const hasBoth = hasPayin && hasPayout;

  const currencies = new Set<CurrencyCode>([
    ...(Object.keys(totalPayin) as CurrencyCode[]),
    ...(Object.keys(totalPayout) as CurrencyCode[]),
  ]);

  return Array.from(currencies).map((currency) => {
    const payinSum = totalPayin[currency] || 0;
    const payoutSum = totalPayout[currency] || 0;
    const amount = hasBoth
      ? payinSum - payoutSum
      : Math.abs(payinSum || payoutSum);
    return { amount, currency, payinSum, payoutSum };
  });
};

type InvoiceItemsSectionProps = {
  items: ItemTableItem[];
  products: Product[];
  users: User[];
  onAddItem?: (item: Omit<ItemTableItem, 'id'>) => void;
  onUpdateItem?: (id: string, item: Omit<ItemTableItem, 'id'>) => void;
  onRemoveItem?: (id: string) => void;
  userBalances?: UserBalances[];
  showUnbalancedWarning?: boolean;
  children?: React.ReactNode;
};

export const InvoiceItemsSection = ({
  items,
  products,
  users,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  userBalances,
  showUnbalancedWarning = true,
  children,
}: InvoiceItemsSectionProps): JSX.Element => {
  const isEditable =
    onAddItem !== undefined ||
    onUpdateItem !== undefined ||
    onRemoveItem !== undefined;

  const { isTotalBalanced } = useMemo(() => getTotalsFromItems(items), [items]);

  const totalAmountForUserView = useMemo(
    () => getTotalsForUserView(items),
    [items]
  );

  return (
    <>
      <LayoutRow
        height="small"
        justify="start"
        hide={!(showUnbalancedWarning && !isTotalBalanced)}
      >
        <TextCell
          text="Errors"
          textSize="xmd"
          align="left"
          border="right"
          textType="secondary"
        />
        <TextCell
          text="Invoice unbalanced - Pay ins ≠ Pay outs"
          textSize="xmd"
          fullWidth
          align="left"
          border="none"
          textType="warning"
        />
      </LayoutRow>

      <ItemTable
        data-testid="payin-items-table"
        items={items}
        products={products}
        users={users}
        onAddItem={onAddItem}
        onUpdateItem={onUpdateItem}
        onRemoveItem={onRemoveItem}
        userBalances={userBalances}
      />

      <div className="w-full">
        <div className="flex items-stretch border-b-[0.5px] border-main-200 w-full min-h-[2lh]">
          <div
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 border-r-[0.5px] border-main-200'
            )}
            aria-hidden
          />
          <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 text-main-500 font-FragmentMono text-xmd border-r-[0.5px] border-main-200 aspect-square">
            ↗
          </div>
          <div className="flex flex-row justify-between items-center gap-x-f1 p-f1 flex-1 min-w-0">
            <span className="text-xmd text-main-500">Payins</span>
            <span className="flex flex-row justify-end gap-x-f1">
              {totalAmountForUserView.map((amount) => {
                const precision = currencyPrecisionMap[amount.currency];
                return (
                  <FormatBillingAmount
                    key={amount.currency}
                    amount={amount.payinSum}
                    currencyCode={amount.currency}
                    currencyPrecision={precision}
                  />
                );
              })}
            </span>
          </div>
        </div>
        <div className="flex items-stretch border-b-[0.5px] border-main-200 w-full min-h-[2lh]">
          <div
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 border-r-[0.5px] border-main-200'
            )}
            aria-hidden
          />
          <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 text-main-500 font-FragmentMono text-xmd border-r-[0.5px] border-main-200 aspect-square">
            ↙
          </div>
          <div className="flex flex-row justify-between items-center gap-x-f1 p-f1 flex-1 min-w-0">
            <span className="text-xmd text-main-500">Payouts</span>
            <span className="flex flex-row justify-end gap-x-f1">
              {totalAmountForUserView.map((amount) => {
                const precision = currencyPrecisionMap[amount.currency];
                return (
                  <FormatBillingAmount
                    key={amount.currency}
                    amount={amount.payoutSum}
                    currencyCode={amount.currency}
                    currencyPrecision={precision}
                  />
                );
              })}
            </span>
          </div>
        </div>
      </div>

      {children}
    </>
  );
};
